import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { useShowAlert } from 'hooks';
import { useFetchWithStatus } from 'hooks/fetchWithStatus';
import { createRestrictionRequest } from 'services/Restrictions';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import AutocompleteSelect, {
  statusHelperTextRenderOption
} from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPTextEditor from 'UI/components/molecules/FPTextEditor';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { TINY_WITH_IMAGES_CONFIG } from 'UI/constants/config';
import { UIStatus } from 'UI/constants/status';

import { isRestrictionType, RESTRICTION_TYPES } from '../../constants';

import { getRestrictionSubStatusEndpoint } from './ProfileRestrictionDrawer.constants';
import { useStyles } from './ProfileRestrictionDrawer.styled';

const COPIES = strings.inventoryProfiles.common.restriction.drawer;

/**
 * @param {Object} props
 * @param {number | string} props.entityId
 * @param {number | string} props.entityTypeId: Candidate:1 | HA: 3 | Name:4
 * @param {Function} props.onClose
 * @param {boolean} props.open
 * @param {string} props.selectedRestrictionType: doNotContact, doNotEngage
 */
export const ProfileRestrictionDrawer = ({
  entityId,
  entityTypeId,
  onClose,
  open,
  selectedRestrictionType
}) => {
  const classes = useStyles();
  const { showAlert } = useShowAlert();
  const editorRef = useRef(null);
  const [content, setContent] = useState('');
  const [selectedSubStatus, setSelectedSubStatus] = useState(null);
  const [uiState, setUiState] = useState({ isSaving: false });
  const [responseMessage, setResponseMessage] = useState('');
  const hasResponseMessage = Boolean(responseMessage);

  const restrictionCopy =
    strings.inventoryProfiles.common.restriction[selectedRestrictionType] ?? '';
  const restrictionBodyCopy =
    selectedRestrictionType === RESTRICTION_TYPES.doNotEngage
      ? COPIES.doNotEngageBody
      : COPIES.doNotContactBody;

  useEffect(() => {
    if (open) return;

    setContent('');
    setSelectedSubStatus(null);
    setUiState({ isSaving: false });
    setResponseMessage('');
  }, [open]);

  const subStatusEndpoint = useMemo(
    () => getRestrictionSubStatusEndpoint({ entityTypeId, selectedRestrictionType }),
    [entityTypeId, selectedRestrictionType]
  );

  const {
    state: { results: subStatusesOptions, status }
  } = useFetchWithStatus(subStatusEndpoint);
  const isLoadingSubStatuses = status === UIStatus.Loading;
  const hasSubStatusOptions = Array.isArray(subStatusesOptions) && subStatusesOptions.length > 0;

  const initEditor = (e, editor) => {
    editorRef.current = editor;
  };

  const handleEditorChange = editorValue => {
    setContent(editorValue);
  };

  const handleSubStatusChange = (_name, value) => {
    setSelectedSubStatus(value);
  };

  const handleSubmit = async () => {
    try {
      if (!content || uiState.isSaving) return;

      setUiState(prev => ({ ...prev, isSaving: true }));

      const body = {
        type: selectedRestrictionType,
        entityId,
        entityTypeId,
        ...(selectedSubStatus?.id && { entitySubStatusId: selectedSubStatus.id }),
        reason: content
      };
      const response = await createRestrictionRequest(body);

      if (response?.data?.message) {
        setResponseMessage(response.data.message);
      }
    } catch (error) {
      showAlert({
        title: strings.formatString(COPIES.error.title, {
          restriction: restrictionCopy
        }),
        body: COPIES.error.body,
        severity: 'error',
        error
      });
    } finally {
      setUiState(prev => ({ ...prev, isSaving: false }));
    }
  };

  const handleClose = (_event, closeReason) => {
    if (closeReason === 'backdropClick') return;
    onClose && onClose();
  };

  const buttonDefinitions = [
    {
      props: {
        onClick: onClose,
        text: strings.shared.ui.close,
        variant: 'outlined'
      },
      GridItemProps: {
        md: 'auto'
      }
    },
    {
      props: {
        disabled: !content || uiState.isSaving || hasResponseMessage,
        initialText: COPIES.send,
        isSaving: uiState.isSaving,
        onClick: handleSubmit,
        onProgressText: strings.shared.ui.sending,
        variant: 'contained'
      },
      GridItemProps: {
        md: 3
      },
      mode: 'save'
    }
  ];

  if (!isRestrictionType(selectedRestrictionType)) return null;

  return (
    <FPDrawer
      CardHeaderProps={{
        title: strings.formatString(COPIES.title, {
          restriction: restrictionCopy
        })
      }}
      FPDrawerProps={{
        open
      }}
      onClose={handleClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <When condition={responseMessage}>
        <Alert severity="info">
          <AlertTitle>{strings.inventoryProfiles.common.restriction.requestInProgress}</AlertTitle>
          {responseMessage}
        </Alert>
      </When>

      <When condition={!responseMessage}>
        <Typography variant="body2" className={classes.bodyText}>
          {strings.formatString(restrictionBodyCopy, {
            restriction: restrictionCopy
          })}
        </Typography>
      </When>

      <When condition={isLoadingSubStatuses}>
        <Box marginTop={4}>
          <LinearProgress />
        </Box>
      </When>

      <When condition={hasSubStatusOptions}>
        <Box marginTop={4}>
          <AutocompleteSelect
            disabled={uiState.isSaving}
            name="entitySubStatusId"
            noMargin
            onSelect={handleSubStatusChange}
            options={subStatusesOptions}
            placeholder={strings.formatString(COPIES.subStatusLabel, {
              restriction: restrictionCopy
            })}
            renderOption={option =>
              statusHelperTextRenderOption({
                ...option,
                helperText: option.description
              })
            }
            selectedValue={selectedSubStatus}
          />
        </Box>
      </When>

      <Box marginTop={4}>
        <FPTextEditor
          config={TINY_WITH_IMAGES_CONFIG}
          containerClassName={classes.editorContainer}
          disabled={uiState.isSaving || hasResponseMessage}
          onChange={handleEditorChange}
          onInit={initEditor}
          value={content}
        />
      </Box>
    </FPDrawer>
  );
};
